import { loadBaseCSS } from '../shared/button-utils.js';
import { upgradeProperty } from '../shared/form-control.js';
import { primitiveStyles } from '../shared/primitive-utils.js';

/**
 * Related actions with native Tab order; selection belongs to each control.
 * @element qds-button-group
 * @attr {string} label - Accessible name describing the related actions.
 * @attr {'horizontal'|'vertical'} orientation - Visual direction; default horizontal.
 * @property {string} label - Group accessible name.
 * @property {'horizontal'|'vertical'} orientation - Visual direction.
 * @slot - Native buttons or QDS action controls, retaining their own Tab stops.
 * @csspart group - Wrapping group surface.
 */
export class QdsButtonGroup extends HTMLElement {
  static get observedAttributes(): string[] { return ['label', 'orientation']; }
  constructor() {
    super();
    this.attachShadow({ mode: 'open' }).innerHTML = `<style>${primitiveStyles}
      :host { display: inline-block; max-width: 100%; }
      [part=group] { display: flex; flex-wrap: wrap; align-items: center; gap: .5rem; }
      :host([orientation=vertical]) [part=group] { flex-direction: column; align-items: stretch; }
    </style><div role="group" part="group"><slot></slot></div>`;
    loadBaseCSS();
  }
  connectedCallback(): void { for (const name of ['label', 'orientation']) upgradeProperty(this, name); this._sync(); }
  attributeChangedCallback(): void { this._sync(); }
  get label(): string { return this.getAttribute('label') || ''; }
  set label(value: string) { this.setAttribute('label', value ?? ''); }
  get orientation(): 'horizontal' | 'vertical' { return this.getAttribute('orientation') === 'vertical' ? 'vertical' : 'horizontal'; }
  set orientation(value: 'horizontal' | 'vertical') { this.setAttribute('orientation', value); }
  private _sync(): void {
    const group = this.shadowRoot!.querySelector('[part=group]')!;
    if (this.label) group.setAttribute('aria-label', this.label); else group.removeAttribute('aria-label');
  }
}
if (!customElements.get('qds-button-group')) customElements.define('qds-button-group', QdsButtonGroup);
